import React, { useEffect, useMemo, useState } from 'react';
import { Marker, InfoWindow, Polygon } from '@react-google-maps/api';
import styles from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle';
import GridContainer from 'components/Grid/GridContainer';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';

import GridItem from 'components/Grid/GridItem';

import AddressesSimilarityTable from './AddressesSimilarityTable';
import Legend from 'components/Tvendo/Common/Legend';
import { wsGet, wsGetAll } from 'services/WService';
import { WithApp } from 'contexts/appContext';
import wkt from 'wkt';
import moment from 'moment';
import LayerLegend from 'components/Tvendo/Common/LayerLegend';
import LayerCombo from 'components/Tvendo/Common/LayerCombo';
import useLayerContext from 'contexts/layerContext';
import GoogleMapCustom from 'components/Tvendo/Common/GoogleMapCustom';

const useStyles = makeStyles(() => styles);

const defaultMapCenter = {
  lat: -14.075338295892307,
  lng: -75.73423638548219,
};

function CustomerIncompleteMap(props) {
  const classes = useStyles();
  const [ubigeos, setUbigeos] = useState([]);
  const [openInfo, setOpenInfo] = useState(false);
  const [infoData, setInfoData] = useState({});
  const { customer, addresses } = props;
  const { businessPartner, setBusinessPartner } = props;
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [businessLinesUnilever, setBusinessLinesUnilever] = useState([]);
  const [selectedBusinessLine, setSelectedBusinessLine] = useState(null);
  const { selectedLayer } = useLayerContext();
  const { zoneId } = props;
  const [zone, setZone] = useState();

  async function getUbigeo() {
    let response = await wsGetAll('base/ubigeo');
    if (response.success) {
      setUbigeos(response.data);
    }
  }
  const [mapCenter, setMapCenter] = useState(defaultMapCenter);
  const { marker, setMarker } = props;
  const [zoneGeometry, setZoneGeometry] = useState();
  const [routeGeometries, setRouteGeometries] = useState([]);
  const [routeDescription, setRouteDescription] = useState();

  async function listBusinessLines() {
    let res = await wsGetAll('base/business-line?base_business_partner_id=1');
    setBusinessLinesUnilever(res.data);
  }

  function mapGeometry(point) {
    return { lng: point[0], lat: point[1] };
  }

  async function getZone(zoneId) {
    let response = await wsGet('base/zone', zoneId, {
      with_addresses: 'N',
    });
    if (response.success !== true) return;
    let { routes, ...zone } = response.data;
    setZone(zone);
    if (zone.geometry === null) return;
    let zoneGeometry = wkt.parse(zone.geometry);

    zoneGeometry.coordinates[0] = zoneGeometry.coordinates[0].map(mapGeometry);

    setZoneGeometry(zoneGeometry);
    let routeGeometries = routes
      .filter((route) => route.geometry !== null)
      .map((route) => {
        let routeGeometry = wkt.parse(route.geometry);
        routeGeometry.coordinates[0] = routeGeometry.coordinates[0].map(mapGeometry);
        routeGeometry.description = route.description;
        routeGeometry.id = route.id;
        routeGeometry.layer = route.layer;
        return routeGeometry;
      });
    setRouteGeometries(routeGeometries);
  }

  useEffect(() => {
    getUbigeo();
    listBusinessLines();
  }, []);

  useEffect(() => {
    if (businessLinesUnilever.length === 0) return;
    if (customer.base_business_line_id === undefined) return;
    if (customer.base_business_line_id === null) return;
    const match = businessLinesUnilever.find(
      (item) => parseInt(item.id) === parseInt(customer.base_business_line_id),
    );
    if (match === undefined) return;
    setBusinessPartner((bp) =>
      bp.business_line === match.id ? bp : { ...bp, business_line: match.id },
    );
  }, [businessLinesUnilever, customer]);

  useEffect(() => {
    if (zoneId === undefined) return;
    getZone(zoneId);
  }, [zoneId]);

  useEffect(() => {
    if (customer.latitude === undefined) return;
    let coordenates = {
      lat: parseFloat(customer.latitude),
      lng: parseFloat(customer.longitude),
    };
    if (coordenates.lat !== 0) {
      setMapCenter(coordenates);
    }
    setMarker(coordenates);
  }, [customer]);

  const filteredRouteGeometries = useMemo(() => {
    if (routeGeometries.length === 0) return routeGeometries;
    if (selectedLayer === undefined) return routeGeometries;
    return routeGeometries.filter((route) => route.layer.id === selectedLayer.id);
  }, [selectedLayer, routeGeometries]);

  return (
    <GridContainer style={{ width: 'calc(100% + 0px)', margin: '0' }}>
      <GridItem sm={12}>
        <h4
          style={{
            color: '#22ca22',
            fontWeight: 500,
          }}
        >
          Ruta: {routeDescription || 'Pase el mouse por un polígono'}
        </h4>
        {zone && <h4 style={{ fontWeight: 500 }}>Zona: {zone.description}</h4>}
      </GridItem>
      <GridItem xs={12} md={6} lg={6} xl={6} className={classes.container}>
        <GoogleMapCustom
          mapContainerStyle={{
            width: '100%',
            height: '500px',
          }}
          zoom={20}
          center={mapCenter}
          onClick={() => {
            setOpenInfo(false);
          }}
        >
          {marker !== null ? (
            marker.lat !== 0 ? (
              <Marker
                draggable
                position={marker}
                onDragEnd={(e) => {
                  setMarker({
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  });
                }}
                icon={'/img/marker.png'}
              />
            ) : undefined
          ) : undefined}
          {filteredRouteGeometries.map((route, index) => {
            return (
              <Polygon
                key={index}
                onMouseOver={() => setRouteDescription(route.description)}
                onMouseOut={() => setRouteDescription()}
                options={{
                  fillColor: route.layer.color,
                }}
                paths={route.coordinates}
              />
            );
          })}

          {addresses.map((address, i) => {
            return !!address ? (
              <Marker
                position={{
                  lat: parseFloat(address.latitude),
                  lng: parseFloat(address.longitude),
                }}
                key={i}
                onClick={() => {
                  setOpenInfo(true);
                  setInfoData(address);
                }}
                icon={
                  selectedAddresses.length === 0
                    ? undefined
                    : !!selectedAddresses.find((selected) => selected.id === address.id)
                    ? '/img/mark_yellow.png'
                    : undefined
                }
              />
            ) : undefined;
          })}

          {openInfo ? (
            <InfoWindow
              position={{
                lat: parseFloat(infoData.latitude),
                lng: parseFloat(infoData.longitude),
              }}
              onCloseClick={() => {
                setOpenInfo(false);
                setInfoData({});
              }}
              options={{
                pixelOffset: {
                  width: 0,
                  height: -25,
                },
              }}
            >
              <div className={classes.divStyle}>
                <li>
                  <span>Nombre: </span>
                  {infoData.CardName}
                </li>
                <li>
                  <span>Dir: </span>
                  {infoData.Address2}
                </li>
              </div>
            </InfoWindow>
          ) : undefined}
        </GoogleMapCustom>
        <Legend
          legends={[
            {
              imageSrc: '/img/marker.png',
              title: 'Nueva ubicación',
            },
            {
              imageSrc: '/img/GoogleMarker.png',
              title: 'Direcciones cercanas',
            },
            {
              imageSrc: '/img/mark_yellow.png',
              title: 'Direcciones similares',
            },
          ]}
        />
      </GridItem>
      <GridItem xs={12} md={6} lg={6} xl={6} className={classes.container}>
        <GridContainer>
          <GridItem xs={12}>
            <LayerLegend />
          </GridItem>
          <GridItem xs={12}>
            <LayerCombo />
          </GridItem>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Vendedor'}
              value={customer.SlpName}
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </GridItem>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Fecha / Hora de creación'}
              value={moment(customer.created_at).format('DD-MM-YYYY HH:mm')}
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </GridItem>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Observaciones'}
              value={customer.observations}
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </GridItem>
          <GridItem xs={12}>
            <FormControl fullWidth margin="normal" size="small" required>
              <InputLabel>Giro de negocio (unilever)</InputLabel>
              <Select
                value={businessPartner.business_line || ''}
                required
                onChange={(e) => {
                  setBusinessPartner((bp) => ({ ...bp, business_line: e.target.value }));
                }}
              >
                {businessLinesUnilever.map((item) => {
                  return (
                    <MenuItem value={item.id} key={item.id}>
                      {item.description}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12}>
            <AddressesSimilarityTable
              addresses={addresses}
              addressString={businessPartner.Address}
              setOpenInfo={setOpenInfo}
              setInfoData={setInfoData}
              routes={routeGeometries}
              setSelectedAddresses={setSelectedAddresses}
            />
          </GridItem>
        </GridContainer>
      </GridItem>
    </GridContainer>
  );
}

export default WithApp(CustomerIncompleteMap);
